import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { getPairMeta } from "@components/pages/Futures/components/Quotes/pairMeta";
import {
  getPositionPnlRoi,
  normalizeOrderSide,
} from "@helpers/formatOrders";
import { formatPrice } from "@helpers/formatPrice";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import "./OrderCardItem.sass";

const toNumber = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : null;
};

const formatMoney = (value) => {
  if (value == null || !Number.isFinite(value)) return "—";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatSignedMoney = (value) => {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (value > 0) return `+$${abs}`;
  if (value < 0) return `-$${abs}`;
  return `$${abs}`;
};

const formatDollarPrice = (value) => {
  const numeric = toNumber(value);
  if (numeric == null) return "—";
  return `$${formatPrice(numeric)}`;
};

const formatCloseTimeAmPm = (value) => {
  if (value == null || value === "") return "—";

  const str = String(value).trim();
  const dayMonthYear = str.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/,
  );
  if (dayMonthYear) {
    let hours = Number(dayMonthYear[4]);
    const minutes = dayMonthYear[5];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  }

  const yearMonthDay = str.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})/,
  );
  if (yearMonthDay) {
    let hours = Number(yearMonthDay[4]);
    const minutes = yearMonthDay[5];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  }

  const date = new Date(str);
  if (!Number.isNaN(date.getTime())) {
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  }

  return "—";
};

export const OrderCardItem = ({
  order,
  markPrice,
  isHistory = false,
  isPosition = false,
  onPairClick,
  onActionClick,
  actionLabel,
  onCardClick,
}) => {
  const rawSymbol = order?.symbol ?? order?.pair ?? "";
  const baseAsset = String(rawSymbol)
    .toUpperCase()
    .replace(/(USDT|USDC|BUSD)$/i, "");
  const meta = getPairMeta(rawSymbol || `${baseAsset}USDT`);

  const { isSellSide } = normalizeOrderSide(order?.side);
  const isLong = !isSellSide;

  const { pnlValue } = getPositionPnlRoi(order, markPrice);

  const leverage = order?.leverage ? `${order.leverage}x` : null;
  const entryPrice =
    order?.entry_price ??
    order?.fill_price ??
    order?.price ??
    order?.avg_price ??
    0;
  const exitPrice = order?.closed_price ?? order?.close_price ?? null;
  const currentPrice = markPrice || entryPrice;

  const wager = toNumber(order?.margin);

  let livePnl = null;
  if (isPosition) {
    livePnl =
      pnlValue != null && Number.isFinite(pnlValue)
        ? pnlValue
        : toNumber(order?.pnl);
  } else if (isHistory) {
    livePnl = toNumber(order?.realized_pnl ?? order?.pnl);
  }

  const payout =
    wager != null && livePnl != null && Number.isFinite(livePnl)
      ? wager + livePnl
      : null;

  const multiplier =
    wager != null && wager !== 0 && payout != null ? payout / wager : null;

  const isProfit = livePnl == null ? true : livePnl >= 0;
  const valueTone = isProfit
    ? "order-card-item__value--positive"
    : "order-card-item__value--negative";

  const multiplierText =
    multiplier != null && Number.isFinite(multiplier)
      ? `${multiplier.toFixed(2)}x`
      : "—";

  const pnlText = isPosition || isHistory ? formatSignedMoney(livePnl) : "—";
  const bottomRightText = isHistory
    ? formatCloseTimeAmPm(order?.closed_at ?? order?.close_time)
    : formatMoney(payout);

  return (
    <article
      className={classnames("order-card-item", {
        "order-card-item--history": isHistory,
        "order-card-item--clickable": onCardClick,
      })}
      onClick={onCardClick}
    >
      <div className="order-card-item__left">
        <div className="order-card-item__icon-wrap">
          {meta?.icon ? (
            <img
              src={meta.icon}
              alt={baseAsset || "coin"}
              className="order-card-item__coin-logo"
            />
          ) : (
            <div className="order-card-item__coin-logo order-card-item__coin-logo--placeholder">
              {baseAsset?.slice(0, 3) || "COIN"}
            </div>
          )}
          <span className="order-card-item__badge">
            {isLong ? (
              <TradeArrowUpIcon size={15} color="#2FA766" />
            ) : (
              <TradeArrowDownIcon size={15} color="#E2464A" />
            )}
          </span>
        </div>

        <div className="order-card-item__info">
          <div className="order-card-item__row order-card-item__row--symbol">
            {onPairClick ? (
              <button
                type="button"
                className="order-card-item__ticker order-card-item__ticker--button"
                onClick={(event) => {
                  event.stopPropagation();
                  onPairClick(rawSymbol);
                }}
              >
                {baseAsset || rawSymbol}
              </button>
            ) : (
              <span className="order-card-item__ticker">
                {baseAsset || rawSymbol}
              </span>
            )}
            <span className="order-card-item__price">
              {formatDollarPrice(entryPrice)}
            </span>
          </div>

          {isHistory ? (
            <>
              <div className="order-card-item__row order-card-item__row--size">
                <span className="order-card-item__wager">
                  {wager != null ? formatMoney(wager) : "—"}
                </span>
                {leverage && (
                  <span className="order-card-item__leverage">{leverage}</span>
                )}
              </div>
              <div className="order-card-item__row order-card-item__row--exit">
                <span className="order-card-item__exit-label">Exit</span>
                <span className="order-card-item__exit-price">
                  {formatDollarPrice(exitPrice)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="order-card-item__row order-card-item__row--meta">
                <span className="order-card-item__subtext">
                  {formatDollarPrice(currentPrice)}
                </span>
              </div>
              <div className="order-card-item__row order-card-item__row--size">
                <span className="order-card-item__wager">
                  {wager != null ? formatMoney(wager) : "—"}
                </span>
                {leverage && (
                  <span className="order-card-item__leverage">{leverage}</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="order-card-item__stats">
        <span className={`order-card-item__multiplier ${valueTone}`}>
          {multiplierText}
        </span>
        <span className={`order-card-item__pnl ${valueTone}`}>{pnlText}</span>
        <span
          className={`order-card-item__total${
            isHistory ? " order-card-item__total--time" : ""
          }`}
        >
          {bottomRightText}
        </span>

        {onActionClick && actionLabel && (
          <button
            type="button"
            className="order-card-item__action-btn"
            onClick={onActionClick}
          >
            {actionLabel}
          </button>
        )}
      </div>
    </article>
  );
};

OrderCardItem.propTypes = {
  order: PropTypes.object.isRequired,
  markPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isHistory: PropTypes.bool,
  isPosition: PropTypes.bool,
  onPairClick: PropTypes.func,
  onActionClick: PropTypes.func,
  actionLabel: PropTypes.string,
  onCardClick: PropTypes.func,
};

export default OrderCardItem;
